import React, { useState, useEffect } from 'react';
import { conditionsAPI } from '../../../services/api';
import '../Shared/FormStyles.scss';

const ConditionsManager = () => {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConditions();
  }, []);

  const fetchConditions = async () => {
    try {
      const response = await conditionsAPI.getAllAdmin();
      setConditions(response.data);
    } catch (err) {
      setError('Failed to fetch conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await conditionsAPI.update(editing._id, formData);
        setSuccess('Condition updated successfully');
      } else {
        await conditionsAPI.create(formData);
        setSuccess('Condition created successfully');
      }
      resetForm();
      fetchConditions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save condition');
    }
  };

  const handleEdit = (condition) => {
    setEditing(condition);
    setFormData({
      title: condition.title || '',
      description: condition.description || '',
      isActive: condition.isActive !== undefined ? condition.isActive : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this condition?')) return;
    try {
      await conditionsAPI.delete(id);
      setSuccess('Condition deleted successfully');
      fetchConditions();
    } catch (err) {
      setError('Failed to delete condition');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      description: '',
      isActive: true,
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="conditions-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Condition' : 'Add New Condition'}</h2>
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
          <strong>Note:</strong> This section manages full condition details (with descriptions). 
          To manage the simple list of conditions displayed on the home page, use the <strong>"Home Page Conditions List"</strong> tab in the Home Content section.
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Conditions</h2>
        {conditions.length === 0 ? (
          <div className="empty-state">No conditions found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conditions.map((condition) => (
                <tr key={condition._id}>
                  <td>{condition.title}</td>
                  <td>{condition.description}</td>
                  <td>{condition.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(condition)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(condition._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ConditionsManager;
